import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { parseAsBoolean, parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ListFilter, ListFilters } from '@/components/list-filters'
import { LoadingButton } from '@/components/loading-button'
import { SearchInput } from '@/components/search-input'
import { SearchSelect } from '@/components/search-select'
import { StatusPill } from '@/components/status-pill'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { resultCount } from '@/lib/utils'
import {
  ACCOUNT_LABELS,
  ACCOUNT_TONES,
  SUPPLIER_KIND_TONES,
  accountStatus,
  termsPill,
} from '@/lib/suppliers'
import {
  SUPPLIER_KIND_LABELS,
  useSelectAllSources,
  useSourceChange,
  useSourcePeople,
  useSourcePreview,
  useSources,
  useSuppliers,
  type SourceBulkAction,
  type SourceFilters,
  type SourceRow,
} from '@/api/sourcing'

const POSITIONS = ['all', 'preferred', 'backup'] as const
const METHODS = ['all', 'email', 'online', 'other'] as const
const KINDS = ['all', 'direct', 'rep_group', 'distributor', 'online_retailer', 'buying_group'] as const
const POSITION_LABELS = { all: 'All', preferred: 'First choice', backup: 'Backups' }
const METHOD_LABELS = { all: 'All', email: 'Emailed PO', online: 'Online', other: 'Another way' }
const KIND_LABELS = {
  all: 'All',
  direct: 'Direct',
  rep_group: 'Rep groups',
  distributor: 'Distributors',
  online_retailer: 'Online retailers',
  buying_group: 'Buying groups',
}
const ACTION_LABELS: Record<SourceBulkAction, string> = {
  remove_person: 'Remove person',
  make_to: 'Send POs to',
  remove_source: 'Remove source',
  make_preferred: 'Make first choice',
}

function Selector({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
}) {
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-border text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    />
  )
}

function Manufacturer({ row }: { row: SourceRow }) {
  return (
    <div className="space-y-1">
      <Link
        to="/manufacturers/$manuId"
        params={{ manuId: row.manufacturer.uuid }}
        className="font-medium text-primary hover:underline"
      >
        {row.manufacturer.name}
      </Link>
      <div className="flex flex-wrap gap-1">
        <StatusPill tone={row.rank === 1 ? 'success' : 'neutral'}>
          {row.rank === 1 ? 'First choice' : `Backup ${row.rank - 1}`}
        </StatusPill>
        {row.product_scope && <StatusPill tone="neutral">{row.product_scope}</StatusPill>}
      </div>
    </div>
  )
}

function Source({ row }: { row: SourceRow }) {
  return (
    <div className="space-y-1">
      {row.supplier ? (
        <Link
          to="/suppliers/$supplierId"
          params={{ supplierId: row.supplier.uuid }}
          className="font-medium text-primary hover:underline"
        >
          {row.supplier.name}
        </Link>
      ) : (
        <p className="font-medium">Direct</p>
      )}
      <div className="flex flex-wrap gap-1">
        {row.supplier && (
          <StatusPill tone={SUPPLIER_KIND_TONES[row.supplier.kind]}>
            {SUPPLIER_KIND_LABELS[row.supplier.kind]}
          </StatusPill>
        )}
        {row.order_method !== 'email' && (
          <StatusPill tone="warning">{row.order_method === 'online' ? 'Online' : 'Another way'}</StatusPill>
        )}
      </div>
    </div>
  )
}

function Recipients({ row }: { row: SourceRow }) {
  if (row.order_method !== 'email' && !row.po_email) {
    return <span className="text-xs text-muted-foreground">Placed by staff</span>
  }
  return (
    <div className="space-y-0.5 text-xs">
      <p className="break-words">
        <span className="text-muted-foreground">POs </span>
        {row.po_email ?? (
          <span className="font-medium text-amber-700 dark:text-amber-300">No contact</span>
        )}
        {(row.po_cc_emails ?? []).length > 0 && (
          <span className="text-muted-foreground"> cc {(row.po_cc_emails ?? []).join(', ')}</span>
        )}
      </p>
      {row.spec_credit_email && row.spec_credit_email !== row.po_email && (
        <p className="break-words">
          <span className="text-muted-foreground">Spec credit </span>
          {row.spec_credit_email}
        </p>
      )}
    </div>
  )
}

function Standing({ row }: { row: SourceRow }) {
  const account = accountStatus(row.account_status)
  const terms = row.rank === 1 ? termsPill(row.manufacturer) : null
  return (
    <div className="flex flex-wrap gap-1">
      <StatusPill tone={ACCOUNT_TONES[account]}>{ACCOUNT_LABELS[account]}</StatusPill>
      {terms && <StatusPill tone={terms.tone}>{terms.label}</StatusPill>}
    </div>
  )
}

function who(recipients: { po_email?: string | null; po_cc_emails?: string[] }) {
  if (!recipients.po_email) return 'No contact'
  const copied = recipients.po_cc_emails ?? []
  return copied.length ? `${recipients.po_email} cc ${copied.join(', ')}` : recipients.po_email
}

/** What the change will do, from the server's dry run, then the go-ahead. */
function ChangeDialog({
  action,
  optionIds,
  rows,
  contactId,
  onClose,
  onDone,
}: {
  action: SourceBulkAction
  optionIds: number[]
  rows: SourceRow[]
  contactId?: number
  onClose: () => void
  onDone: () => void
}) {
  const needsPerson = action === 'remove_person' || action === 'make_to'
  const { data: people } = useSourcePeople()
  const [person, setPerson] = useState<number | undefined>(contactId)
  const ready = !needsPerson || person !== undefined
  const { data: preview, isFetching } = useSourcePreview(
    ready ? { action, option_ids: optionIds, contact_id: person } : null,
  )
  const change = useSourceChange()

  const onRows = new Set(rows.flatMap((row) => (row.contacts ?? []).map((contact) => contact.contact_id)))
  const options = (people ?? [])
    .filter((option) => !onRows.size || onRows.has(option.contact_id))
    .map((option) => ({
      value: String(option.contact_id),
      label: option.email,
      description: option.name,
    }))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{ACTION_LABELS[action]}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {needsPerson && (
            <SearchSelect
              value={person === undefined ? undefined : String(person)}
              options={options}
              onChange={(value) => setPerson(value ? Number(value) : undefined)}
              placeholder="Choose a person"
              searchPlaceholder="Search people"
              ariaLabel="Person"
            />
          )}
          {ready && isFetching && <Skeleton className="h-16 w-full" />}
          {preview && !isFetching && (
            <>
              <p>
                {resultCount(preview.manufacturers ?? 0, 'manufacturer')}
                {(preview.sources_removed ?? 0) > 0 &&
                  `, ${resultCount(preview.sources_removed ?? 0, 'source')} removed`}
              </p>
              {(preview.supplier_wide ?? []).length > 0 && (
                <p>Every brand bought through {(preview.supplier_wide ?? []).join(', ')} follows.</p>
              )}
              {(preview.changed ?? []).length > 0 && (
                <ul className="max-h-56 divide-y overflow-y-auto text-xs">
                  {(preview.changed ?? []).map((item) => (
                    <li key={item.manufacturer.id} className="py-1.5">
                      <p className="font-medium">{item.manufacturer.name}</p>
                      <p className="break-words text-muted-foreground">
                        {who(item.before)} → <span className="text-foreground">{who(item.after)}</span>
                      </p>
                    </li>
                  ))}
                </ul>
              )}
              {(preview.no_po_contact ?? []).length > 0 && (
                <p className="text-amber-700 dark:text-amber-300">
                  No one left to email POs: {(preview.no_po_contact ?? []).map((item) => item.name).join(', ')}
                </p>
              )}
              {(preview.blocked ?? []).map((item) => (
                <p key={item.manufacturer.id} className="text-red-700 dark:text-red-300">
                  {item.manufacturer.name}: {item.reason}
                </p>
              ))}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            size="sm"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            disabled={!preview || isFetching || (preview.manufacturers ?? 0) === 0}
            loading={change.isPending}
            loadingText="Saving..."
            onClick={() =>
              change.mutate(
                { action, option_ids: optionIds, contact_id: person },
                { onSuccess: onDone },
              )
            }
          >
            Apply
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SourcesPage() {
  const [search, setSearch] = useUrlSearchText()
  const [filters, setFilters] = useQueryStates({
    supplier: parseAsString,
    person: parseAsInteger,
    position: parseAsStringLiteral(POSITIONS).withDefault('all'),
    method: parseAsStringLiteral(METHODS).withDefault('all'),
    kind: parseAsStringLiteral(KINDS).withDefault('all'),
    missing: parseAsBoolean.withDefault(false),
    page: parseAsInteger.withDefault(1),
  })
  const debouncedSearch = useDebouncedValue(search)
  const query: SourceFilters = {
    search: debouncedSearch || undefined,
    supplier: filters.supplier ?? undefined,
    contact_id: filters.person ?? undefined,
    position: filters.position === 'all' ? undefined : filters.position,
    order_method: filters.method === 'all' ? undefined : filters.method,
    kind: filters.kind === 'all' ? undefined : filters.kind,
    no_po_contact: filters.missing || undefined,
  }
  const { data, isLoading } = useSources(query, filters.page)
  const { data: suppliers } = useSuppliers()
  const { data: people } = useSourcePeople()
  const selectAll = useSelectAllSources(query)
  const [selected, setSelected] = useState<Map<number, SourceRow | null>>(new Map())
  const [action, setAction] = useState<SourceBulkAction | null>(null)
  const rows = data?.items ?? []
  const allOnPage = rows.length > 0 && rows.every((row) => selected.has(row.id))
  const active =
    [filters.supplier, filters.person].filter(Boolean).length +
    [filters.position, filters.method, filters.kind].filter((value) => value !== 'all').length +
    (filters.missing ? 1 : 0)

  function toggle(row: SourceRow, checked: boolean) {
    setSelected((current) => {
      const next = new Map(current)
      if (checked) next.set(row.id, row)
      else next.delete(row.id)
      return next
    })
  }

  function toggleAll(checked: boolean) {
    setSelected((current) => {
      const next = new Map(current)
      for (const row of rows) {
        if (checked) next.set(row.id, row)
        else next.delete(row.id)
      }
      return next
    })
  }

  const clear = () => {
    setSelected(new Map())
    setAction(null)
  }

  const header = (
    <Selector checked={allOnPage} onChange={toggleAll} label="Select this page" />
  )

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <h1 className="text-2xl font-bold tracking-tight">Buying sources</h1>

      <ListFilters
        count={data ? resultCount(data.total, 'source') : undefined}
        active={active}
        onClear={() =>
          void setFilters({
            supplier: null,
            person: null,
            position: null,
            method: null,
            kind: null,
            missing: null,
            page: 1,
          })
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search by manufacturer, source or email..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Source">
          <SearchSelect
            value={filters.supplier ?? undefined}
            options={[
              { value: 'direct', label: 'Direct' },
              ...(suppliers ?? []).map((supplier) => ({
                value: String(supplier.id),
                label: supplier.name,
                description: SUPPLIER_KIND_LABELS[supplier.kind],
              })),
            ]}
            onChange={(value) => void setFilters({ supplier: value ?? null, page: 1 })}
            allLabel="All"
            placeholder="All"
            searchPlaceholder="Search sources"
            ariaLabel="Source"
          />
        </ListFilter>
        <ListFilter label="Person">
          <SearchSelect
            value={filters.person === null ? undefined : String(filters.person)}
            options={(people ?? []).map((person) => ({
              value: String(person.contact_id),
              label: person.email,
              description: resultCount(person.manufacturers, 'manufacturer'),
            }))}
            onChange={(value) => void setFilters({ person: value ? Number(value) : null, page: 1 })}
            allLabel="All"
            placeholder="All"
            searchPlaceholder="Search people"
            ariaLabel="Person"
          />
        </ListFilter>
        <ListFilter label="Position">
          <Select
            value={filters.position}
            onValueChange={(value) =>
              void setFilters({ position: value as (typeof POSITIONS)[number], page: 1 })
            }
          >
            <SelectTrigger aria-label="Position" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {POSITIONS.map((value) => (
                <SelectItem key={value} value={value}>
                  {POSITION_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Kind">
          <Select
            value={filters.kind}
            onValueChange={(value) => void setFilters({ kind: value as (typeof KINDS)[number], page: 1 })}
          >
            <SelectTrigger aria-label="Kind" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((value) => (
                <SelectItem key={value} value={value}>
                  {KIND_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Ordered by">
          <Select
            value={filters.method}
            onValueChange={(value) =>
              void setFilters({ method: value as (typeof METHODS)[number], page: 1 })
            }
          >
            <SelectTrigger aria-label="Ordered by" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {METHODS.map((value) => (
                <SelectItem key={value} value={value}>
                  {METHOD_LABELS[value]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="PO contact">
          <Select
            value={filters.missing ? 'missing' : 'all'}
            onValueChange={(value) => void setFilters({ missing: value === 'missing', page: 1 })}
          >
            <SelectTrigger aria-label="PO contact" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="missing">No PO contact</SelectItem>
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {selected.size > 0 && (
        <div
          aria-label="Change selected sources"
          className="flex flex-wrap items-center gap-2 rounded-lg bg-muted/50 p-2"
        >
          <p className="px-1 text-sm font-medium tabular-nums">{selected.size} selected</p>
          {data && selected.size < data.total && (
            <Button
              variant="link"
              size="sm"
              className="h-8 px-1"
              disabled={selectAll.isPending}
              onClick={() =>
                selectAll.mutate(undefined, {
                  onSuccess: (ids) =>
                    setSelected((current) => {
                      const next = new Map(current)
                      for (const id of ids) if (!next.has(id)) next.set(id, null)
                      return next
                    }),
                })
              }
            >
              Select all {data.total}
            </Button>
          )}
          <div className="ml-auto flex flex-wrap gap-1.5">
            {(Object.keys(ACTION_LABELS) as SourceBulkAction[]).map((value) => (
              <Button key={value} variant="outline" size="sm" onClick={() => setAction(value)}>
                {ACTION_LABELS[value]}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={clear}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div role="status" aria-label="Loading sources" className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-16 w-full" />
          ))}
        </div>
      ) : !rows.length ? (
        <div className="py-12 text-center text-muted-foreground">No sources found</div>
      ) : (
        <>
          <div className="hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">{header}</TableHead>
                  <TableHead>Manufacturer</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Who gets email</TableHead>
                  <TableHead>Account and terms</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id} className="align-top">
                    <TableCell>
                      <Selector
                        checked={selected.has(row.id)}
                        onChange={(checked) => toggle(row, checked)}
                        label={`Select ${row.manufacturer.name}`}
                      />
                    </TableCell>
                    <TableCell className="max-w-[260px] whitespace-normal">
                      <Manufacturer row={row} />
                    </TableCell>
                    <TableCell className="max-w-[240px] whitespace-normal">
                      <Source row={row} />
                    </TableCell>
                    <TableCell className="max-w-[340px] whitespace-normal">
                      <Recipients row={row} />
                    </TableCell>
                    <TableCell className="max-w-[260px] whitespace-normal">
                      <Standing row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="space-y-2 lg:hidden">
            {rows.map((row) => (
              <div key={row.id} className="flex gap-3 rounded-lg border bg-card p-3">
                <Selector
                  checked={selected.has(row.id)}
                  onChange={(checked) => toggle(row, checked)}
                  label={`Select ${row.manufacturer.name}`}
                />
                <div className="min-w-0 flex-1 space-y-2">
                  <Manufacturer row={row} />
                  <Source row={row} />
                  <Recipients row={row} />
                  <Standing row={row} />
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between py-1">
            <p className="text-sm text-muted-foreground">
              Page {data?.page ?? 1} of {data?.pages ?? 1}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void setFilters({ page: Math.max(1, filters.page - 1) })}
                disabled={filters.page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void setFilters({ page: filters.page + 1 })}
                disabled={filters.page >= (data?.pages ?? 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {action && (
        <ChangeDialog
          action={action}
          optionIds={[...selected.keys()]}
          rows={[...selected.values()].filter((row): row is SourceRow => row !== null)}
          contactId={filters.person ?? undefined}
          onClose={() => setAction(null)}
          onDone={clear}
        />
      )}
    </div>
  )
}
